/**
 * EditorContextBanner — banners persistentes en el editor del artifact
 * para visibilidad de cross-refs (ADR-0002 Fase 10.f.2 + 10.e).
 *
 * Renderiza hasta dos banners en cascada según el estado:
 *
 *   1. **Drift detectado** (rojo, prioritario): el source de alguna
 *      cross-ref consumida cambió en una nueva versión canon. El owner
 *      debería revisar si su contenido sigue siendo consistente.
 *      Se activa cuando `driftPending=true` (flag PG seteado por el
 *      drift detection task).
 *
 *   2. **Referencias en borrador** (ámbar, informativo): el agente
 *      generó esta versión usando contenido de OTROS documentos que aún
 *      estaban en borrador. Cuando esos documentos se aprueben, los
 *      cambios pueden afectar la consistencia.
 *      Se activa cuando hay ≥1 captura con `was_canon_at_capture=false`.
 *
 * Ambos banners pueden coexistir (renderizan en orden de prioridad).
 *
 * Componente puramente presentacional — el caller carga la version y
 * pasa las capturas + flag.
 */

import { AlertTriangle, FileText } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import type { CrossArtifactCapture } from '../../api/artifacts'

export interface EditorContextBannerProps {
  /** Lista de `cross_artifact_captures` de la version en edición. Vacía
   * o ausente = sin banner de "referencias en borrador". */
  captures: CrossArtifactCapture[]
  /** Flag `cross_ref_drift_pending` de la version. True = mostrar banner
   * de drift. */
  driftPending: boolean
}

export function EditorContextBanner({
  captures,
  driftPending,
}: EditorContextBannerProps) {
  const { t } = useTranslation('projects')

  const draftCaptures = captures.filter((c) => c.was_canon_at_capture === false)
  const hasDraftRefs = draftCaptures.length > 0

  // Dedup por par `(source_artifact_id, source_section_id)` — múltiples
  // capturas para la misma sección consumidora (varias secciones del
  // consumer derivan de la misma sección del source) renderean como un
  // solo chip para no inundar la UI con duplicados.
  const uniqueDraftRefs = new Map<
    string,
    CrossArtifactCapture
  >()
  for (const c of draftCaptures) {
    const key = `${c.source_artifact_id}::${c.source_section_id}`
    if (!uniqueDraftRefs.has(key)) {
      uniqueDraftRefs.set(key, c)
    }
  }
  const uniqueRefList = Array.from(uniqueDraftRefs.values())

  if (!driftPending && !hasDraftRefs) return null

  return (
    <div className="mb-4 space-y-2" data-testid="editor-context-banners">
      {driftPending ? (
        <div
          className="rounded-md border border-critical/40 bg-critical-soft/40 px-4 py-3 flex items-start gap-3"
          data-testid="editor-context-banner-drift"
        >
          <AlertTriangle className="w-4 h-4 text-critical shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display text-sm font-semibold text-ink">
              {t('official_viewer.cross_ref_banner.drift_title')}
            </p>
            <p className="mt-0.5 font-body text-xs text-ink-muted">
              {t('official_viewer.cross_ref_banner.drift_body')}
            </p>
          </div>
        </div>
      ) : null}

      {hasDraftRefs ? (
        <div
          className="rounded-md border border-amber-500/40 bg-amber-500/5 px-4 py-3 flex items-start gap-3"
          data-testid="editor-context-banner-draft-refs"
        >
          <FileText className="w-4 h-4 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="font-display text-sm font-semibold text-ink">
              {t('official_viewer.cross_ref_banner.draft_refs_title', {
                count: uniqueRefList.length,
              })}
            </p>
            <p className="mt-0.5 font-body text-xs text-ink-muted">
              {t('official_viewer.cross_ref_banner.draft_refs_body')}
            </p>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {uniqueRefList.map((c, idx) => {
                const technicalRef = `${c.source_template_key}.${c.source_section_id}`
                const hasLabels =
                  c.source_template_name && c.source_section_title
                return (
                  <li
                    key={`${c.source_artifact_id}-${c.source_section_id}-${idx}`}
                    className={
                      hasLabels
                        ? 'inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 text-[11px] text-ink'
                        : 'inline-flex items-center gap-1 rounded-md border border-amber-500/40 bg-amber-500/10 px-2 py-0.5 font-mono text-[11px] text-ink'
                    }
                    data-testid="editor-context-banner-draft-ref-chip"
                    title={technicalRef}
                  >
                    {hasLabels ? (
                      <>
                        <span className="font-medium">
                          {c.source_template_name}
                        </span>
                        <span className="text-ink-subtle">·</span>
                        <span>{c.source_section_title}</span>
                      </>
                    ) : (
                      technicalRef
                    )}
                  </li>
                )
              })}
            </ul>
          </div>
        </div>
      ) : null}
    </div>
  )
}
